import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ClientItemId } from 'owa-client-ids';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getDefaultSlrAppRibbonButtonStyles } from 'owa-command-ribbon-styles';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import {
    onDeleteClicked,
    onFlagUnflagClicked,
    onForwardClicked,
    onMarkAsReadUnreadClicked,
    onReplyAllClicked,
    onReplyClicked,
    onPrintClicked,
    onReplyWithMeetingClicked,
} from '../../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
} from '../../../../util/createMailRibbonButton';
import { getReadRibbonId } from '../../../../util/getReadRibbonId';
import { getFlagUnflagSections, getJunkSections, getRulesSections } from '../../../sections';

export const getDeleteControl = computedFn(
    (itemId?: ClientItemId): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.DeleteItem);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.DeleteItem, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            onDeleteClicked(itemId),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.DeleteItem),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getJunkControl = computedFn(
    (itemId?: ClientItemId, targetWindow?: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.JunkFlyout);
        return createMailAppFlyoutAnchor(
            getReadRibbonId(MailRibbonControlId.JunkFlyout, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            {
                sections: [
                    {
                        controls: getJunkSections(
                            MailRibbonTabId.Tab_Home,
                            undefined,
                            itemId,
                            targetWindow
                        ),
                    },
                ],
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.JunkFlyout),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getRulesControl = computedFn(
    (itemId?: ClientItemId, targetWindow?: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Rules);
        return createMailAppFlyoutAnchor(
            getReadRibbonId(MailRibbonControlId.Rules, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            {
                sections: getRulesSections(itemId, targetWindow),
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.Rules),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getReplyControl = computedFn(
    (itemId?: ClientItemId, targetWindow?: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RespondReply);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.RespondReply, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            onReplyClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.RespondReply)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getReplyAllControl = computedFn(
    (itemId?: ClientItemId, targetWindow?: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RespondReplyAll);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.RespondReplyAll),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            onReplyAllClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.RespondReplyAll)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getForwardControl = computedFn(
    (itemId?: ClientItemId, targetWindow?: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RespondForward);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.RespondForward, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            onForwardClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.RespondForward)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getReplyWithMeetingControl = computedFn(
    (itemId?: ClientItemId): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ReplyWithMeeting);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.ReplyWithMeeting, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            onReplyWithMeetingClicked(itemId),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Read)],
                getKeytipString(MailRibbonControlId.ReplyWithMeeting)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getUnreadReadControl = computedFn(
    (itemId?: ClientItemId): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ReadUnread);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.ReadUnread, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            true /*isPopout*/,
            onMarkAsReadUnreadClicked(itemId),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.ReadUnread)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getFlagUnflagControl = computedFn(
    (itemId?: ClientItemId): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.FlagUnflag);
        return createMailAppSplitButton(
            getReadRibbonId(MailRibbonControlId.FlagUnflag, itemId),
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            true /*isPopout*/,
            { sections: getFlagUnflagSections(MailRibbonTabId.Tab_Home, itemId) },
            onFlagUnflagClicked(undefined, undefined, itemId),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.FlagUnflag),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);

export const getPrintControl = computedFn(
    (itemId?: ClientItemId, targetWindow?: Window): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Print);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.Print, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onPrintClicked(itemId, targetWindow),
            constructKeytip([], getKeytipString(MailRibbonControlId.Print)),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
